'use client'

import React, { FC, ReactElement } from 'react'
import styles from './InscriptionContent.module.css'
import {
	ORD_ROOT_URL,
	TEXT_COLLECTION_SLUGS,
	CDN_PREVIEW_URL,
	COLLECTION_IMAGE_OVERRIDES,
	COLLECTION_SLUGS,
	FULL_LAYOUT_RENDER_COLLECTION_SLUGS
} from '@/utils/constants'
import { getInscriptionContentRenderTarget } from '@/utils/inscription-content-url'
import { DMT_RENDERERS, extractDmtBlk, getDmtImageUrl } from '@/utils/dmt-renderers'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import InscriptionText from '@/components/inscription-text'
import InscriptionJson from '@/components/inscription-json'
import Iframe from '@/components/iframe'
import formatHiddenChars from '@/utils/format-hidden-chars'
import isFungible from '@/utils/is-fungible'
import Fungible from './fungible'
import BitmapImage from './bitmap-image'

import { Roboto_Mono } from 'next/font/google'
import NameInscriptionContent from '@/components/name-inscription-content'
import BitmapArt from '@/components/bitmap-art'
import { useBitmapLive } from '@/components/bitmap-art/live-context'
import { Bitmap3dToggle } from '@/components/bitmap-art/bitmap-3d'

const font = Roboto_Mono({ subsets: ['latin'], weight: '400' })
const ModelViewer = dynamic(() => import('./model-viewer'), { ssr: false })
const DEFAULT_NATCATS_RENDERER_URL = 'https://renderer.ordinalswallet.com/api'
const NATCATS_RENDERER_URL =
	process.env.NEXT_PUBLIC_NATCATS_RENDERER_URL || DEFAULT_NATCATS_RENDERER_URL
const NATCATS_RENDERER_VERSION = '2'
const NATCATS_PROFILE_IMAGE_URL =
	'https://creator-hub-prod.s3.us-east-2.amazonaws.com/ord-dmtnatcats_pfp_1708896003459.png'
const STATIC_COLLECTION_IMAGE_URLS: Record<string, string> = {
	runestone: '/collections/runestone/render.png'
}

function getCollectionFallbackImageUrl(collection: any): string | null {
	const slug = collection?.slug
	if (slug === 'dmtnatcats') {
		return collection?.icon?.replace?.('turbo', 'cdn') || NATCATS_PROFILE_IMAGE_URL
	}
	if (slug && COLLECTION_IMAGE_OVERRIDES[slug]) {
		return COLLECTION_IMAGE_OVERRIDES[slug]
	}
	if (slug && COLLECTION_SLUGS.includes(slug)) {
		return `/collections/${slug}/icon.png`
	}
	if (collection?.icon) {
		return String(collection.icon).replace('turbo', 'cdn')
	}
	const iconInscriptionId = collection?.icon_inscription?.id || collection?.icon_inscription_id
	if (iconInscriptionId) {
		return `${CDN_PREVIEW_URL}/${iconInscriptionId}?v=1`
	}
	return null
}

function getNatcatsRendererUrl(baseUrl: string, blk: string) {
	return `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(blk)}?v=${NATCATS_RENDERER_VERSION}`
}

const InscriptionContent: FC<{
	isDetail?: boolean
	className?: string
	textClasName?: string
	inscription: any
	smallCard?: boolean
	/** First few above-the-fold cards. Every other small card stays lazy. */
	eager?: boolean
	showPricePer?: boolean
}> = (props): ReactElement => {
	const { escrow, collection, id, meta } = props.inscription
	const content_type = props.inscription.effective_content_type ?? props.inscription.content_type
	const bitmapLive = useBitmapLive()

	const collectionFallbackImageUrl = React.useMemo(
		() => getCollectionFallbackImageUrl(collection),
		[collection]
	)
	const imageErrorKey = [
		id,
		content_type,
		collection?.slug,
		collection?.icon,
		meta?.name,
		escrow?.name,
		props.inscription?.blk,
		collectionFallbackImageUrl
	]
		.map((value) => String(value ?? ''))
		.join('|')
	const [imgErrorKey, setImgErrorKey] = React.useState<string | null>(null)
	const [fallbackImgErrorKey, setFallbackImgErrorKey] = React.useState<string | null>(null)
	const [natcatsRendererFallbackKey, setNatcatsRendererFallbackKey] = React.useState<string | null>(
		null
	)
	const imgError = imgErrorKey === imageErrorKey
	const fallbackImgError = fallbackImgErrorKey === imageErrorKey

	// The screenshot renderer can hang for over a minute on heavy recursive
	// HTML before answering 503, leaving the card blank the whole time. Give it
	// a bounded wait, then show the collection icon like any other failed image.
	const RENDERER_TIMEOUT_MS = 8000
	const RENDERER_MAX_RETRIES = 3
	const screenshotImgRef = React.useRef<HTMLImageElement | null>(null)
	const [rendererTimedOutKey, setRendererTimedOutKey] = React.useState<string | null>(null)
	const rendererTimedOut = rendererTimedOutKey === imageErrorKey
	// After the timeout the placeholder shows, but the render keeps loading
	// behind it (the renderer caches at the CDN once done) and swaps in on load.
	const [lateLoadedKey, setLateLoadedKey] = React.useState<string | null>(null)
	const lateLoaded = lateLoadedKey === imageErrorKey
	// A busy renderer answers 503 with Retry-After; retry a few times with backoff.
	const [rendererRetry, setRendererRetry] = React.useState<{ key: string; n: number }>({
		key: '',
		n: 0
	})
	const retryCount = rendererRetry.key === imageErrorKey ? rendererRetry.n : 0

	const handleImageError = (_e: React.SyntheticEvent<HTMLImageElement>) => {
		setImgErrorKey(imageErrorKey)
	}

	const handleScreenshotError = () => {
		if (retryCount < RENDERER_MAX_RETRIES) {
			const n = retryCount + 1
			const key = imageErrorKey
			window.setTimeout(() => {
				setRendererRetry({ key, n })
			}, 5000 * n)
			// Show the placeholder while we wait for the retry.
			setRendererTimedOutKey(key)
			return
		}
		setImgErrorKey(imageErrorKey)
	}

	const renderCollectionFallback = (alt?: string, extraClassName?: string) => {
		if (!collectionFallbackImageUrl || fallbackImgError) {
			return <div className={classNames(props.className, 'bg-accent')} />
		}
		return (
			<img
				className={classNames(props.className, styles.image, extraClassName)}
				loading="lazy"
				src={collectionFallbackImageUrl}
				onError={() => setFallbackImgErrorKey(imageErrorKey)}
				alt={alt || collection?.name || collection?.slug || 'Collection icon'}
			/>
		)
	}

	const fullLayoutRenderSize = collection?.slug
		? FULL_LAYOUT_RENDER_COLLECTION_SLUGS[collection.slug]
		: undefined
	const { url, showScreenshot, is_image } = React.useMemo(
		() =>
			getInscriptionContentRenderTarget({
				id,
				contentType: content_type,
				preferStaticRenderer: !props.isDetail,
				rendererSize: fullLayoutRenderSize,
				slug: collection?.slug
			}),
		[props.isDetail, content_type, id, fullLayoutRenderSize, collection?.slug]
	)
	const isHtml = Boolean(content_type?.includes('text/html'))

	React.useEffect(() => {
		if (!showScreenshot || !isHtml || rendererTimedOut) return
		const timer = window.setTimeout(() => {
			const img = screenshotImgRef.current
			if (img && !(img.complete && img.naturalWidth > 0)) {
				setRendererTimedOutKey(imageErrorKey)
			}
		}, RENDERER_TIMEOUT_MS)
		return () => window.clearTimeout(timer)
	}, [showScreenshot, isHtml, rendererTimedOut, imageErrorKey, url])

	//content_type === 'no_content_type'
	//const is_image = content_type?.includes('image')

	if (['goosinals', 'fractalgoose'].includes(collection?.slug)) {
		if (imgError) {
			return renderCollectionFallback(meta?.name || collection?.slug || 'Inscription')
		}
		return (
			<img
				className={classNames(props.className, styles.image)}
				loading="lazy"
				src={url}
				onError={handleImageError}
				alt={meta?.name || collection?.slug || 'Inscription'}
			/>
		)
	}

	if (['bitmap'].includes(collection?.slug)) {
		// On the bitmap collection page and the item page the bitmap is drawn
		// live: the block's transactions laid out and flown in, the same picture
		// the static thumbnail shows everywhere else.
		if (props.isDetail || bitmapLive) {
			return (
				<div className={classNames(props.className, styles.bitmapArt)}>
					<BitmapArt
						height={meta?.name || escrow?.name || ''}
						canvasSize={props.isDetail ? 600 : 300}
						className="size-full"
						showTxCount
					/>
					{props.isDetail && <Bitmap3dToggle />}
				</div>
			)
		}
		return <BitmapImage key={id} id={id} name={meta?.name || escrow?.name} className={classNames(props.className, styles.image, styles.bitmap)} />
	}

	if (['ordimap'].includes(collection?.slug)) {
		if (imgError) {
			return renderCollectionFallback(meta?.name || 'Inscription')
		}
		return (
			<img
				className={classNames(props.className, styles.image)}
				loading="lazy"
				src={`${CDN_PREVIEW_URL}/${id}?v=1`}
				onError={handleImageError}
				alt={meta?.name || 'Inscription'}
			/>
		)
	}

	if (collection?.slug === 'dmtnatcats') {
		const blk = extractDmtBlk(props.inscription)
		if (!blk) {
			return renderCollectionFallback(meta?.name || escrow?.name || 'Natcats', styles.natcat)
		}
		const primaryRendererUrl = getNatcatsRendererUrl(NATCATS_RENDERER_URL, blk)
		const canonicalRendererUrl = getNatcatsRendererUrl(DEFAULT_NATCATS_RENDERER_URL, blk)
		const shouldTryCanonicalRenderer =
			primaryRendererUrl !== canonicalRendererUrl && natcatsRendererFallbackKey === imageErrorKey
		const src = shouldTryCanonicalRenderer ? canonicalRendererUrl : primaryRendererUrl
		if (imgError) {
			return renderCollectionFallback(meta?.name || escrow?.name || 'Natcats', styles.natcat)
		}
		return (
			<img
				className={classNames(props.className, styles.image, styles.natcat)}
				loading="lazy"
				src={src}
				onError={
					!shouldTryCanonicalRenderer && primaryRendererUrl !== canonicalRendererUrl
						? () => setNatcatsRendererFallbackKey(imageErrorKey)
						: handleImageError
				}
				alt={meta?.name || escrow?.name || 'Natcats'}
			/>
		)
	}

	const staticCollectionImageUrl = collection?.slug
		? STATIC_COLLECTION_IMAGE_URLS[collection.slug]
		: null
	if (staticCollectionImageUrl) {
		if (imgError) {
			return renderCollectionFallback(
				meta?.name || collection?.name || collection.slug,
				styles.natcat
			)
		}
		return (
			<img
				className={classNames(props.className, styles.image, styles.natcat)}
				loading="lazy"
				src={collection?.slug === 'runestone' && !props.isDetail ? '/collections/runestone/icon.webp' : staticCollectionImageUrl}
				onError={handleImageError}
				alt={meta?.name || collection?.name || collection.slug}
			/>
		)
	}

	if (collection?.slug === 'chaos') {
		const blk =
			meta?.name?.replace?.(/[^0-9]/g, '') ||
			escrow?.name?.replace?.(/[^0-9]/g, '') ||
			meta?.attributes?.find?.((a: any) => a.trait_type === 'blk')?.value ||
			props.inscription?.name?.replace?.(/[^0-9]/g, '') ||
			props.inscription?.blk ||
			''
		if (!blk) return <div className={classNames(props.className, 'bg-accent')} />
		return (
			<Iframe
				className={classNames(props.className, styles.chaos)}
				src={`/chaos/render-inscription.html?blk=${encodeURIComponent(blk)}`}
				title={`Chaos ${blk}`}
			/>
		)
	}

	if (collection?.slug && DMT_RENDERERS[collection.slug]) {
		const blk = extractDmtBlk(props.inscription)
		const dmtUrl = getDmtImageUrl(collection.slug, props.inscription, blk)
		if (!dmtUrl)
			return renderCollectionFallback(`${collection.slug} ${blk || ''}`.trim(), styles.natcat)
		if (imgError)
			return renderCollectionFallback(
				meta?.name || `${collection.slug} ${blk || ''}`.trim(),
				styles.natcat
			)
		return (
			<img
				className={classNames(props.className, styles.image, styles.natcat)}
				loading="lazy"
				src={dmtUrl}
				onError={handleImageError}
				alt={meta?.name || `${collection.slug} ${blk || ''}`.trim()}
			/>
		)
	}

	if (collection?.slug?.startsWith('brc420-')) {
		return (
			<InscriptionText
				className={classNames(font.className, props.className)}
				inscriptionId={id}
				textClassName={props?.textClasName as any}
				collection={collection}
			/>
		)
	}

	if (isFungible(collection?.slug) && props.showPricePer) {
		return <Fungible inscription={props.inscription} className={props.className} />
	}

	if (TEXT_COLLECTION_SLUGS.includes(collection?.slug)) {
		return (
			<NameInscriptionContent
				className={props.className}
				textClassName={props.textClasName}
				inscription={props.inscription}
				smallCard={props?.smallCard}
			/>
		)
	}

	return (
		<>
			{typeof window !== 'undefined' && content_type?.includes('model') && !showScreenshot && (
				<ModelViewer className={props.className} src={url} />
			)}
			{(is_image || showScreenshot) && !imgError && (!rendererTimedOut || lateLoaded) && (
				<img
					key={`shot-${retryCount}`}
					ref={screenshotImgRef}
					width={props.smallCard ? 150 : 640}
					height={props.smallCard ? 150 : 640}
					className={classNames(props.className, styles.image, {
						[styles.punks]: collection?.slug == 'bitcoinpunks',
						[styles.smoothScale]: Boolean(fullLayoutRenderSize)
					})}
					loading={props.eager ? 'eager' : 'lazy'}
					src={url}
					alt={formatHiddenChars(meta?.name)}
					onError={showScreenshot ? handleScreenshotError : handleImageError}
				/>
			)}
			{showScreenshot && !imgError && rendererTimedOut && !lateLoaded && (
				<img
					key={`late-${retryCount}`}
					src={url}
					alt=""
					aria-hidden
					style={{ display: 'none' }}
					onLoad={() => setLateLoadedKey(imageErrorKey)}
					onError={handleScreenshotError}
				/>
			)}
			{(is_image || showScreenshot) &&
				(imgError || (rendererTimedOut && !lateLoaded)) &&
				renderCollectionFallback(formatHiddenChars(meta?.name))}
			{content_type?.includes('audio') && (
				<audio controls className={classNames(props.className, styles.audio)}>
					<source src={url} type={content_type} />
				</audio>
			)}
			{content_type?.includes('video') && (
				<video
					loop
					muted
					autoPlay
					playsInline
					controls
					className={classNames(props.className, styles.audio)}
				>
					<source src={url} type={content_type} />
				</video>
			)}
			{content_type?.includes('text/plain') && (
				<InscriptionText
					className={classNames(font.className, props.className)}
					inscriptionId={id}
					textClassName={props?.textClasName as any}
					collection={collection}
				/>
			)}
			{content_type?.includes('application/json') && (
				<InscriptionJson
					className={classNames(font.className, props.className)}
					isDetail={props.isDetail}
					imageClassName={styles.image}
					inscriptionId={id}
				/>
			)}
			{content_type?.includes('application/pdf') && (
				<object
					data={url}
					type="application/pdf"
					width="100%"
					height="100%"
					className={props.className}
					aria-label="Inscription PDF document"
				>
					<a href={url}>Open PDF inscription</a>
				</object>
			)}
			{content_type === 'no_content_type' && !showScreenshot && (
				<Iframe src={`${ORD_ROOT_URL}/preview/${id}`} className={props.className} />
			)}
			{content_type?.includes('text/html') && !showScreenshot && (
				<Iframe src={url} className={props.className} />
			)}
			{content_type === 'gallery_item' && !imgError && (
				<img
					className={classNames(props.className, styles.image)}
					loading="lazy"
					src={`${CDN_PREVIEW_URL}/${id}?v=1`}
					onError={handleImageError}
					alt={formatHiddenChars(meta?.name)}
				/>
			)}
			{content_type === 'gallery_item' &&
				imgError &&
				renderCollectionFallback(formatHiddenChars(meta?.name))}
			{/* Unknown content_type (e.g. a global/cross-marketplace sale row whose
			    inscription isn't in the collection's loaded set, so we only have an
			    id) — render the CDN preview by id instead of a blank square. The
			    other branches all key off a known content_type; without this a
			    content_type-less row showed nothing. Strictly additive: only fires
			    when no other branch would. */}
			{!content_type && !imgError && (
				<img
					className={classNames(props.className, styles.image)}
					loading="lazy"
					src={`${CDN_PREVIEW_URL}/${id}?v=1`}
					onError={handleImageError}
					alt={formatHiddenChars(meta?.name) || collection?.slug || 'Inscription'}
				/>
			)}
			{!content_type &&
				imgError &&
				renderCollectionFallback(
					formatHiddenChars(meta?.name) || collection?.slug || 'Inscription'
				)}
		</>
	)
}

export default InscriptionContent
